import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { PageHomeOneComponent } from './pages/page-home-one/page-home-one.component';
import { RootComponent } from './components/root/root.component';
import { PageOffcanvasCartComponent } from './pages/page-offcanvas-cart/page-offcanvas-cart.component';
import { moved } from './shared/interceptors/moved.guard';
import { environment } from 'src/environments/environment';
import { withLanguagePrefixes } from './shared/culture/culture-routes';
import { CultureSellerGuard } from './shared/culture/culture.module';


const routes: Routes = [
    {
        path: '',
        component: RootComponent,
        data: {
            // Header layout. Choose one of ['classic', 'compact'].
            headerLayout: 'classic',
            // Dropcart type. Choose one of ['dropdown', 'offcanvas'].
            dropcartType: 'offcanvas'
        },
        children: [
            // The home page; at /bn the same page with the home fragments' Bangla rows (data.lang, from the /bn copy of
            // these routes: withLanguagePrefixes below). cultureContent: a page with real text of its own in Bangla, which
            // search engines may index under /bn (CultureService.watch).
            {
                path: '',
                pathMatch: 'full',
                component: PageHomeOneComponent,
                data: { cultureContent: true }
            },
            {
                path: 'products',
                loadChildren: () => import('./modules/shop/shop.module').then(m => m.ShopModule)
            },
            // A seller's own page and its tabs (ShopSetup_Spec.md 2.2): /shop/{slug}, /shop/{slug}/products ... The old
            // /products/shop/{slug} answers a 301 on the server and redirects inside the app (ShopRoutingModule). The Bangla
            // version, for a shop that wrote Bangla text, is the same page under /bn.
            {
                path: 'shop/:shopSlug',
                loadChildren: () => import('./modules/shop-front/shop-front.module').then(m => m.ShopFrontModule),
                data: { cultureContent: true }
            },
            // The shops directory (ShopSetup_Spec.md 2.4).
            {
                path: 'shops',
                loadChildren: () => import('./modules/shop-front/shops-directory.module').then(m => m.ShopsDirectoryModule)
            },
            // The posts; the Bangla ones, and their list, at /bn/blog/{slug}.
            {
                path: 'blog',
                loadChildren: () => import('./modules/blog/blog.module').then(m => m.BlogModule),
                data: { cultureContent: true }
            },
            // Sellers' stories are posts (category "stories") at /sell/stories/{slug}; their list is the blog's tab.
            {
                path: 'sell/stories',
                pathMatch: 'full',
                redirectTo: '/blog?cat=stories'
            },
            {
                path: 'sell/stories',
                loadChildren: () => import('./modules/blog/blog.module').then(m => m.BlogModule),
                data: { cultureContent: true }
            },
            // Where Daraz sends a seller back after they allow the app (the callback registered on the app, Daraz_Module_Spec.md
            // section 0): the page is in the account module, and a redirect keeps the ?code= and ?state= it carries.
            { path: 'daraz/auth/callback', redirectTo: '/account/daraz/callback' },
            // The callback registered on app 506374 (the "ERP IM Chat" category app that runs the whole module, 2026-10-30): the same page.
            { path: 'daraz/chat/auth/callback', redirectTo: '/account/daraz/callback' },
            // Tracking moved to the site's own address, /track-order (?order= kept, as the emails link it).
            moved('account/track-order', '/track-order'),
            {
                path: 'account',
                // The dashboard's labels (the seller set) before its first page, coming in from the storefront.
                canActivateChild: [CultureSellerGuard],
                loadChildren: () => import('./modules/account/account.module').then(m => m.AccountModule)
            },
            // The information pages were under /site/, the theme's prefix, until 2026-09-19. The
            // server answers the old addresses with a 301 (LegacySitePaths); this is the same map
            // for an old link followed inside the app - straight to where a page is now.
            {
                path: 'site',
                children: [
                    { path: '', pathMatch: 'full', redirectTo: '/about' },
                    moved('about-us', '/about'),
                    moved('sell-with-primehat', '/sell'),
                    moved('contact-us', '/contact'),
                    moved('why-trust-and-choose-primehat-for-your-shopping', '/trust'),
                    { path: ':page', redirectTo: '/:page' },
                ]
            },
            // The pages that moved to shorter addresses on 2026-10-15 (routes.md); the server answers them with a 301.
            moved('about-us', '/about'),
            moved('sell-with-primehat', '/sell'),
            moved('contact-us', '/contact'),
            moved('why-trust-and-choose-primehat-for-your-shopping', '/trust'),
            // The information pages - help, trust, about, contact, FAQ, selling, terms, privacy, refunds - at the root:
            // /help/delivery, and their Bangla versions at /bn/trust. After the named sections, so a path one of them owns
            // never loads this module; an address nothing matches falls through to the not-found page.
            {
                path: '',
                loadChildren: () => import('./modules/site/site.module').then(m => m.SiteModule)
            },
            {
                path: '**',
                component: PageNotFoundComponent
            }
        ],
    },
];

// Every route also under /bn (Multilang_Culture_Spec.md 5.3), each copy carrying data.lang: 'bn'. The Bangla home page,
// posts, shop pages and information pages had /bn routes of their own; they are these copies now.
const routesInEveryLanguage = withLanguagePrefixes(routes, (environment as any).cultureLanguages || ['en']);

@NgModule({
    imports: [RouterModule.forRoot(routesInEveryLanguage, {
    scrollPositionRestoration: 'enabled',
    anchorScrolling: 'enabled',
    initialNavigation: 'enabled',
    relativeLinkResolution: 'legacy'
})],
    exports: [RouterModule]
})
export class AppRoutingModule { }
